import {
  countActiveFieldUsers,
  countActiveTeamAdminSeats,
} from "./userRoleAccess";
import {
  RECOMMENDATION_PLAN_SEAT_LIMITS,
  SCALE_SELF_SERVE_MAX,
} from "./planSeatLimits";

export const PLAN_RECOMMENDATION = {
  LAUNCH: "LAUNCH",
  GROWTH: "GROWTH",
  SCALE: "SCALE",
  ENTERPRISE: "ENTERPRISE",
};

export const SELF_SERVE_PLAN_LIMITS = RECOMMENDATION_PLAN_SEAT_LIMITS;

export { SCALE_SELF_SERVE_MAX };

export const OVERAGE_RATES = { backOffice: 35, field: 2 };

const PLAN_RANK = {
  [PLAN_RECOMMENDATION.LAUNCH]: 1,
  [PLAN_RECOMMENDATION.GROWTH]: 2,
  [PLAN_RECOMMENDATION.SCALE]: 3,
  [PLAN_RECOMMENDATION.ENTERPRISE]: 4,
};

const PLAN_DISPLAY_NAMES = {
  [PLAN_RECOMMENDATION.LAUNCH]: "Launch",
  [PLAN_RECOMMENDATION.GROWTH]: "Growth",
  [PLAN_RECOMMENDATION.SCALE]: "Scale",
  [PLAN_RECOMMENDATION.ENTERPRISE]: "Enterprise",
};

const normalizeCount = (value) => Math.max(0, Number(value) || 0);

const tierForBackOffice = (count) => {
  if (count > SCALE_SELF_SERVE_MAX.backOffice) return PLAN_RECOMMENDATION.ENTERPRISE;
  if (count <= SELF_SERVE_PLAN_LIMITS[PLAN_RECOMMENDATION.LAUNCH].backOffice) {
    return PLAN_RECOMMENDATION.LAUNCH;
  }
  if (count <= SELF_SERVE_PLAN_LIMITS[PLAN_RECOMMENDATION.GROWTH].backOffice) {
    return PLAN_RECOMMENDATION.GROWTH;
  }
  return PLAN_RECOMMENDATION.SCALE;
};

const tierForField = (count) => {
  if (count > SCALE_SELF_SERVE_MAX.field) return PLAN_RECOMMENDATION.ENTERPRISE;
  if (count <= SELF_SERVE_PLAN_LIMITS[PLAN_RECOMMENDATION.LAUNCH].field) {
    return PLAN_RECOMMENDATION.LAUNCH;
  }
  if (count <= SELF_SERVE_PLAN_LIMITS[PLAN_RECOMMENDATION.GROWTH].field) {
    return PLAN_RECOMMENDATION.GROWTH;
  }
  return PLAN_RECOMMENDATION.SCALE;
};

const pickHigherTier = (tierA, tierB) => {
  if (
    tierA === PLAN_RECOMMENDATION.ENTERPRISE ||
    tierB === PLAN_RECOMMENDATION.ENTERPRISE
  ) {
    return PLAN_RECOMMENDATION.ENTERPRISE;
  }
  return PLAN_RANK[tierA] >= PLAN_RANK[tierB] ? tierA : tierB;
};

/** Single source of truth for plan recommendations based on active seat counts. */
export function recommend(activeBackOfficeUsers, activeFieldUsers) {
  const backOffice = normalizeCount(activeBackOfficeUsers);
  const field = normalizeCount(activeFieldUsers);
  return pickHigherTier(tierForBackOffice(backOffice), tierForField(field));
}

export function getPlanRecommendationLabel(plan) {
  return PLAN_DISPLAY_NAMES[plan] || plan;
}

export function mapProductNameToRecommendation(productName = "") {
  const normalized = String(productName || "").trim().toLowerCase();
  if (normalized.includes("launch")) return PLAN_RECOMMENDATION.LAUNCH;
  if (normalized.includes("growth") || normalized === "plus") {
    return PLAN_RECOMMENDATION.GROWTH;
  }
  if (normalized.includes("scale") || normalized.includes("professional")) {
    return PLAN_RECOMMENDATION.SCALE;
  }
  return null;
}

export function getOverageAlternative(activeBackOfficeUsers, activeFieldUsers, recommendedPlan) {
  if (
    recommendedPlan === PLAN_RECOMMENDATION.ENTERPRISE ||
    recommendedPlan === PLAN_RECOMMENDATION.LAUNCH
  ) {
    return null;
  }

  const backOffice = normalizeCount(activeBackOfficeUsers);
  const field = normalizeCount(activeFieldUsers);
  const planOrder = [
    PLAN_RECOMMENDATION.LAUNCH,
    PLAN_RECOMMENDATION.GROWTH,
    PLAN_RECOMMENDATION.SCALE,
  ];
  const recommendedIndex = planOrder.indexOf(recommendedPlan);
  if (recommendedIndex <= 0) return null;

  const lowerPlan = planOrder[recommendedIndex - 1];
  const limits = SELF_SERVE_PLAN_LIMITS[lowerPlan];
  const extraBackOffice = Math.max(0, backOffice - limits.backOffice);
  const extraField = Math.max(0, field - limits.field);

  if (extraBackOffice === 0 && extraField === 0) return null;

  return {
    plan: lowerPlan,
    extraBackOffice,
    extraField,
    monthlyOverageCost:
      extraBackOffice * OVERAGE_RATES.backOffice + extraField * OVERAGE_RATES.field,
  };
}

export function recommendFromUsers(users = [], companyId) {
  const activeBackOffice = countActiveTeamAdminSeats(users, companyId);
  const activeField = countActiveFieldUsers(users, companyId);
  const recommended = recommend(activeBackOffice, activeField);
  const overage = getOverageAlternative(activeBackOffice, activeField, recommended);

  return {
    activeBackOffice,
    activeField,
    recommended,
    overage,
    isEnterprise: recommended === PLAN_RECOMMENDATION.ENTERPRISE,
    label: getPlanRecommendationLabel(recommended),
  };
}

export function buildRecommendationSummary(result) {
  if (!result) return "";
  if (result.isEnterprise) {
    return "Your configuration exceeds our self-serve plans.";
  }
  return `Recommended Plan: ${result.label}`;
}
